import { HttpParams } from '@angular/common/http';
import { Auth } from '@angular/fire/auth';
import { createApi } from '@core/api/base-api';
import { getYear, getMonth } from 'date-fns';
import { Announcement, CreateAnnouncementPayload } from '@core/interfaces/api';
import { toDayBoundsUtc } from './activity-date-bounds';

export interface ActivityFilters {
  siteId: number;
  selectedMonth: Date;
  timezone: string;
}

export interface DayActivityFilters {
  siteId: number;
  selectedDate: Date;
  timezone: string;
}

export interface ActivitiesResponse {
  data: Announcement[];
  totalCount: number;
}

export interface CreateActivityPayload extends CreateAnnouncementPayload {
  // Activity-specific properties that extend the base announcement payload
  // eventType is already defined in CreateAnnouncementPayload with correct type
  // eventStart and eventEnd are already defined in CreateAnnouncementPayload
  // allDay is already defined in CreateAnnouncementPayload
  // location is already defined in CreateAnnouncementPayload
  // roomId is already defined in CreateAnnouncementPayload
  // showOnPrintCalendar is already defined in CreateAnnouncementPayload
  // showOnDigitalSignage is already defined in CreateAnnouncementPayload
  // eventTags is already defined in CreateAnnouncementPayload
  recurrence?: any;
}

export interface UpdateActivityPayload extends Partial<CreateActivityPayload> {
  id: number;
}

export const createActivitiesApi = (auth: Auth) => {
  const api = createApi(auth);

  return {
    getActivitiesForMonth: async (
      filters: ActivityFilters,
    ): Promise<Announcement[]> => {
      const { siteId, selectedMonth } = filters;

      if (!siteId) {
        return [];
      }

      // Use month/year parameters instead of timestamps to avoid timezone issues
      const year = getYear(selectedMonth);
      const month = getMonth(selectedMonth); // Both JavaScript and API use 0-indexed months

      // An invalid selectedMonth yields NaN here, and `month=NaN&year=NaN`
      // collapses to "no date filter" server-side -- an unbounded scan.
      if (!Number.isFinite(year) || !Number.isFinite(month)) {
        throw new Error(
          `Cannot load activities: invalid month (${String(selectedMonth)})`,
        );
      }

      let queryParams = new HttpParams()
        .set('month', month.toString())
        .set('year', year.toString())
        .set('searchBy', JSON.stringify(['styleId']))
        .set('search', '3') // "events" have a styleId of 3 in the announcements table
        .set('sortField', 'eventStart')
        .set('sortOrder', 'asc');

      const response = await api.get<ActivitiesResponse>(
        `/announcements/site/${siteId}`,
        {
          params: queryParams,
        },
      );

      return response.data;
    },

    getActivitiesForDay: async (
      filters: DayActivityFilters,
    ): Promise<Announcement[]> => {
      const { siteId, selectedDate, timezone } = filters;

      if (!siteId) {
        return [];
      }

      // Calculate day boundaries - convert from site timezone to UTC for database query.
      // Throws rather than emitting NaN bounds, which the API would treat as
      // "no date filter" and answer with every announcement for the site.
      const { start, end } = toDayBoundsUtc(selectedDate, timezone);

      let queryParams = new HttpParams()
        .set('eventStart', start.toString())
        .set('eventEnd', end.toString())
        .set('searchBy', JSON.stringify(['styleId']))
        .set('search', '3') // "events" have a styleId of 3 in the announcements table
        .set('sortField', 'eventStart')
        .set('sortOrder', 'asc');

      const response = await api.get<ActivitiesResponse>(
        `/announcements/site/${siteId}`,
        {
          params: queryParams,
        },
      );

      return response.data;
    },

    createActivity: (payload: CreateActivityPayload): Promise<Announcement> =>
      api.post<Announcement>('/announcements', payload),

    updateActivity: (
      id: number,
      payload: Partial<CreateActivityPayload>,
    ): Promise<Announcement> =>
      api.put<Announcement>(`/announcements/update/${id}`, payload),

    deleteActivity: (id: number): Promise<void> =>
      api.put<void>(`/announcements/update/${id}`, { deleted: true }),

    getActivityById: (id: number): Promise<Announcement> =>
      api.get<Announcement>(`/announcements/${id}`),
  };
};

// Angular compatibility layer
export class ActivitiesApi {
  private readonly api: ReturnType<typeof createActivitiesApi>;

  constructor(private auth: Auth) {
    this.api = createActivitiesApi(auth);
  }

  getActivitiesForMonth(filters: ActivityFilters): Promise<Announcement[]> {
    return this.api.getActivitiesForMonth(filters);
  }

  getActivitiesForDay(filters: DayActivityFilters): Promise<Announcement[]> {
    return this.api.getActivitiesForDay(filters);
  }

  createActivity(payload: CreateActivityPayload): Promise<Announcement> {
    return this.api.createActivity(payload);
  }

  updateActivity(
    id: number,
    payload: Partial<CreateActivityPayload>,
  ): Promise<Announcement> {
    return this.api.updateActivity(id, payload);
  }

  deleteActivity(id: number): Promise<void> {
    return this.api.deleteActivity(id);
  }

  getActivityById(id: number): Promise<Announcement> {
    return this.api.getActivityById(id);
  }
}
